import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Inbox } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Header Tasks Button - Shows a clickable icon to navigate to Tasks
 * Displays a count badge of all open tasks (leads + updates)
 * Color-coded based on current stage (win=green, deliver=blue, get-paid=orange)
 */
const HeaderTasksButton = ({ stage = 'deliver' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [totalCount, setTotalCount] = useState(0);

  // Determine stage color
  const getStageColor = () => {
    if (stage === 'win') return 'text-emerald-400';
    if (stage === 'deliver') return 'text-blue-400';
    if (stage === 'get-paid') return 'text-orange-400';
    return 'text-slate-400';
  };

  const getBadgeColor = () => {
    if (stage === 'win') return 'bg-emerald-500';
    if (stage === 'deliver') return 'bg-blue-500';
    if (stage === 'get-paid') return 'bg-orange-500';
    return 'bg-slate-500';
  };

  // Get the capture route for the current stage
  const getTasksRoute = () => {
    if (stage === 'win') return '/tradie/leads-referral';
    if (stage === 'deliver') return '/tradie/log-update';
    if (stage === 'get-paid') return '/tradie/claim';
    return '/tradie/log-update';
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [updatesRes, leadsRes] = await Promise.all([
          axios.get(`${API_URL}/updates/my`),
          axios.get(`${API_URL}/leads/my`)
        ]);
        
        // Filter to only count "open" items (not Done/Won/Lost)
        const openUpdates = updatesRes.data.filter(u => {
          const status = (u.status || 'New').toLowerCase();
          return status !== 'done';
        });
        const openLeads = leadsRes.data.filter(l => {
          const status = (l.status || 'New').toLowerCase();
          return status !== 'done' && status !== 'won' && status !== 'lost';
        });
        
        setTotalCount(openUpdates.length + openLeads.length);
      } catch (error) {
        console.error('Failed to fetch task counts:', error);
      }
    };
    
    fetchCounts();
  }, []);

  const handleClick = () => {
    // Navigate to the capture page for this stage, which has the swipeable Tasks screen
    const route = getTasksRoute();
    
    // If we're already on a capture route, we need to trigger navigation with a state
    // to tell UnifiedCapture to scroll to Tasks screen
    navigate(route, { state: { scrollToTasks: true } });
  };

  return (
    <button
      onClick={handleClick}
      data-testid="header-tasks-btn"
      className="p-2 rounded-lg hover:bg-slate-700 transition-colors relative"
      title="View All Open Tasks"
    >
      <Inbox className={`w-5 h-5 ${getStageColor()}`} />
      {totalCount > 0 && (
        <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center ${getBadgeColor()} text-white`}>
          {totalCount > 99 ? '99+' : totalCount}
        </span>
      )}
    </button>
  );
};

export default HeaderTasksButton;
